import { api } from '@/convex/_generated/api';
import {
  type QueryKey,
  type UseQueryOptions,
  type UseQueryResult,
  useQuery as useTanStackQuery,
} from '@tanstack/react-query';
import {
  type OptionalRestArgsOrSkip,
  useAction,
  useMutation,
  useQueries,
  useQuery as useConvexQuery,
} from 'convex/react';
import {
  getFunctionName,
  makeFunctionReference,
  type FunctionArgs,
  type FunctionReference,
  type FunctionReturnType,
} from 'convex/server';
import type { Value } from 'convex/values';
import { useMemo } from 'react';

// Re-export the API for convenience
export { api };

type QueryStatusResult<T> = {
  data: T | undefined;
  error: Error | null;
  isError: boolean;
  isPending: boolean;
  isSuccess: boolean;
  status: 'error' | 'pending' | 'success';
};

function isTanStackQueryOptions(
  value: unknown
): value is UseQueryOptions<unknown, Error, unknown, QueryKey> {
  return (
    typeof value === 'object' &&
    value !== null &&
    ('queryKey' in value || 'queryFn' in value)
  );
}

export function useQuery<Query extends FunctionReference<'query'>>(
  query: Query,
  ...args: OptionalRestArgsOrSkip<Query>
): QueryStatusResult<Query['_returnType']>;

export function useQuery<
  TQueryFnData = unknown,
  TError = Error,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey,
>(
  options: UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>
): UseQueryResult<TData, TError>;

export function useQuery(
  queryOrOptions: unknown,
  ...args: unknown[]
): QueryStatusResult<unknown> | UseQueryResult<unknown, Error> {
  const isOptions = isTanStackQueryOptions(queryOrOptions);
  const tanStackResult = useTanStackQuery(
    isOptions
      ? queryOrOptions
      : ({
          queryKey: ['__convex_disabled__'],
          queryFn: async () => undefined,
          enabled: false,
        } as UseQueryOptions<unknown, Error, unknown, QueryKey>)
  );
  const data = useConvexQuery(
    (isOptions ? api.users.queries.viewer : queryOrOptions) as never,
    (isOptions ? 'skip' : args[0]) as never
  );

  if (isOptions) {
    return tanStackResult;
  }

  return {
    data: data as unknown | undefined,
    error: null,
    isError: false,
    isPending: data === undefined,
    isSuccess: data !== undefined,
    status: data === undefined ? 'pending' : 'success',
  };
}

/**
 * Like `useQuery`, but a server error comes back as `isError`/`error` instead
 * of throwing into the nearest error boundary. `useQuery` above rethrows, so
 * its `isError` is always false and one failing query takes down the route.
 * Use this for panels and cards that can show their own unavailable state.
 */
export function useQueryWithStatus<Query extends FunctionReference<'query'>>(
  query: Query,
  args: FunctionArgs<Query> | 'skip'
): QueryStatusResult<FunctionReturnType<Query>> {
  const argsKey = args === 'skip' ? null : JSON.stringify(args);
  // `api.module.fn` builds a new reference object on every access, so the
  // request is keyed by function name; keying by identity re-subscribes on
  // every render and loops.
  const queryName = getFunctionName(query);
  const requests = useMemo(() => {
    const request: Record<
      string,
      { query: FunctionReference<'query'>; args: Record<string, Value> }
    > = {};
    if (argsKey !== null) {
      request.result = {
        query: makeFunctionReference<'query'>(queryName),
        args: JSON.parse(argsKey),
      };
    }
    return request;
  }, [argsKey, queryName]);
  const results = useQueries(requests);
  const value: unknown = results.result;
  if (value instanceof Error) {
    return {
      data: undefined,
      error: value,
      isError: true,
      isPending: false,
      isSuccess: false,
      status: 'error',
    };
  }
  const data: FunctionReturnType<Query> | undefined = results.result;
  return {
    data,
    error: null,
    isError: false,
    isPending: data === undefined,
    isSuccess: data !== undefined,
    status: data === undefined ? 'pending' : 'success',
  };
}

// Export standard hooks for mutations and actions
export { useAction, useMutation };
